import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import { useCatalogLanguages, useDeleteMeasurementFamily, useMeasurementFamilies } from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { MeasurementFamilyFormDialog } from '../components/MeasurementFamilyFormDialog';

/** Measurement families (units of measure) — user-story-catalog-013. */
export function MeasurementFamiliesPage() {
  const { t, i18n } = useTranslation('catalog');
  const { canStructureView, canStructureManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);

  const familiesQuery = useMeasurementFamilies({ enabled: canStructureView });
  const languagesQuery = useCatalogLanguages({ enabled: canStructureView });
  const deleteMutation = useDeleteMeasurementFamily();

  const allFamilies = useMemo(() => familiesQuery.data ?? [], [familiesQuery.data]);
  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc).
  const defaultLocale = useMemo(
    () => languagesQuery.data?.find((l) => l.defaultLocale)?.locale,
    [languagesQuery.data],
  );
  const families = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return allFamilies;
    return allFamilies.filter((family) => {
      const label = resolveLocalizedText(family.translations, 'label', i18n.language, '', defaultLocale);
      return family.code.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
    });
  }, [allFamilies, filter, i18n.language, defaultLocale]);

  const editing = editingCode ? (allFamilies.find((f) => f.code === editingCode) ?? null) : null;
  const deleting = deletingCode ? (allFamilies.find((f) => f.code === deletingCode) ?? null) : null;

  const openCreate = () => {
    setEditingCode(null);
    setDialogOpen(true);
  };
  const openEdit = (code: string) => {
    setEditingCode(code);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (!deleting) return;
    const { code } = deleting;
    deleteMutation.mutate(code, {
      onSuccess: () => {
        success(t('measurementFamilies.delete.success', { code }));
        setDeletingCode(null);
      },
      onError: (e) => {
        if (e.code === 'STANDARD_FAMILY_READONLY') {
          notifyError(t('measurementFamilies.delete.errorStandardReadonly'));
        } else if (e.status === 409) {
          notifyError(t('measurementFamilies.delete.errorInUse'));
        } else {
          notifyError(e.message ?? t('measurementFamilies.delete.error'));
        }
        setDeletingCode(null);
      },
    });
  };

  return (
    <SectionScaffold
      title={t('measurementFamilies.title')}
      subtitle={t('measurementFamilies.subtitle')}
      canView={canStructureView}
      settingsTo="/settings/catalog"
      settingsLabel={t('measurementFamilies.settingsLabel')}
      search={{ value: filter, onChange: setFilter, placeholder: t('measurementFamilies.searchPlaceholder') }}
      primaryAction={
        canStructureManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            {t('measurementFamilies.actions.add')}
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {familiesQuery.isLoading ? (
          <LoadingState label={t('measurementFamilies.loading')} />
        ) : familiesQuery.isError ? (
          <ErrorState
            title={t('measurementFamilies.loadError.title')}
            message={familiesQuery.error?.message ?? t('common:error.unknown')}
            onRetry={() => void familiesQuery.refetch()}
          />
        ) : families.length === 0 ? (
          <EmptyState
            title={filter ? t('common:empty.noResults') : t('measurementFamilies.empty.title')}
            description={
              filter
                ? t('measurementFamilies.empty.filteredDescription')
                : t('measurementFamilies.empty.description')
            }
            icon={<StraightenRoundedIcon />}
            action={
              canStructureManage && !filter ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  {t('measurementFamilies.actions.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('measurementFamilies.table.label')}</TableCell>
                  <TableCell>{t('measurementFamilies.table.code')}</TableCell>
                  <TableCell>{t('measurementFamilies.table.standardUnit')}</TableCell>
                  <TableCell align="right">{t('measurementFamilies.table.unitCount')}</TableCell>
                  <TableCell>{t('measurementFamilies.table.status')}</TableCell>
                  <TableCell align="right">{t('measurementFamilies.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {families.map((family) => (
                  <TableRow key={family.code} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {resolveLocalizedText(
                          family.translations,
                          'label',
                          i18n.language,
                          family.code,
                          defaultLocale,
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="code"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                      >
                        {family.code}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                      >
                        {family.standardUnitCode}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{family.units.length}</TableCell>
                    <TableCell>
                      {family.standard ? (
                        <Chip size="small" variant="outlined" label={t('measurementFamilies.standardChip')} />
                      ) : (
                        <Chip
                          size="small"
                          color={family.active ? 'success' : 'default'}
                          label={
                            family.active
                              ? t('measurementFamilies.statusActive')
                              : t('measurementFamilies.statusInactive')
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {family.standard ? (
                        <Tooltip title={t('measurementFamilies.standardReadonlyTooltip')}>
                          <span>
                            <IconButton size="small" disabled aria-label={t('measurementFamilies.standardReadonlyTooltip')}>
                              <LockRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip
                            title={
                              canStructureManage
                                ? t('measurementFamilies.rowActions.edit')
                                : t('measurementFamilies.rowActions.view')
                            }
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={t('measurementFamilies.rowActions.editAria', { code: family.code })}
                              onClick={() => openEdit(family.code)}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canStructureManage && (
                            <Tooltip title={t('measurementFamilies.rowActions.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={t('measurementFamilies.rowActions.deleteAria', { code: family.code })}
                                onClick={() => setDeletingCode(family.code)}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TablePageShell>

      <Dialog open={deletingCode !== null} onClose={() => setDeletingCode(null)}>
        <DialogTitle>{t('measurementFamilies.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('measurementFamilies.delete.body', { code: deleting?.code })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <MeasurementFamilyFormDialog
        open={dialogOpen}
        editing={editing}
        languages={languagesQuery.data ?? []}
        canManage={canStructureManage}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
